import { Link } from "react-router-dom";
import {
  TrendingUp,
  Briefcase,
  Wallet,
  Sprout,
  Heart,
  Upload,
  CheckCircle2,
  Shield,
} from "lucide-react";
import { useAuth } from "../../context/authContext";
import { ROLES } from "../../constants/roles";
import {
  useVentureDashboardHook,
  useVentureDueDiligenceHook,
} from "../../hooks/useVentureHooks";
import { STAGE_LABELS, STAGE_LEVELS } from "../../data/mockData";
import "./dashboard.css";

// ---------------------------------------------------------------------- *
// Due-diligence helpers — mirror the admin ventureDetail page so the
// venture-facing rendering shows identical values to what admins see.
// ---------------------------------------------------------------------- *

const READINESS_META = {
  ready_advanced: { label: "Advanced", tone: "danger" },
  ready: { label: "Investment Ready", tone: "primary" },
  developing: { label: "Developing", tone: "accent" },
  early: { label: "Early Stage", tone: "warning" },
  not_ready: { label: "Not Ready", tone: "muted" },
};

function getRiskLevel(score) {
  if (score >= 80) return "low";
  if (score >= 55) return "medium";
  return "high";
}

function getRiskMeta(level) {
  if (level === "low") return { label: "Low", tone: "success", score: 82 };
  if (level === "average")
    return { label: "Average", tone: "warning", score: 58 };
  if (level === "medium")
    return { label: "Medium", tone: "warning", score: 58 };
  return { label: "High", tone: "danger", score: 36 };
}

function getReadiness(performanceScore) {
  if (performanceScore >= 85) return "ready_advanced";
  if (performanceScore >= 72) return "ready";
  if (performanceScore >= 58) return "developing";
  if (performanceScore >= 40) return "early";
  return "not_ready";
}

function getPhaseOverallScore(rankings) {
  if (!rankings || typeof rankings !== "object") return null;
  const values = Object.values(rankings)
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));
  if (values.length === 0) return null;
  const averageOutOfFive =
    values.reduce((sum, value) => sum + value, 0) / values.length;
  return Math.round((averageOutOfFive / 5) * 100);
}

// Per-phase derived values reuse the same formulas as the single fallback:
//   behavioral = round(performance * 0.88)
//   risk       = getRiskLevel(performance)
//   readiness  = getReadiness(performance)
function derivePhase(score) {
  if (score === null || score === undefined) return null;
  const numeric = Number(score);
  if (!Number.isFinite(numeric)) return null;
  const behavioral = Math.round(numeric * 0.88);
  const level = getRiskLevel(numeric);
  return {
    performance: numeric,
    behavioral,
    riskLevel: level,
    riskMeta: getRiskMeta(level),
    readinessKey: getReadiness(numeric),
  };
}

export default function Dashboard() {
  const { user, token } = useAuth();
  const normalizedRole = String(user?.role ?? "").toLowerCase();
  const roleAllowed = normalizedRole === ROLES.VENTURE;

  const {
    data: apiData,
    isLoading,
    error: queryError,
  } = useVentureDashboardHook({
    token,
    userId: user?.id,
    enabled: roleAllowed,
  });

  // Due-diligence is loaded in parallel with the dashboard. It's OK if it
  // fails or returns null — the section simply falls back to the neutral
  // "no assessment on file" state.
  const { data: dueDiligenceApiData } = useVentureDueDiligenceHook({
    token,
    userId: user?.id,
    enabled: roleAllowed,
  });

  const toNumber = (value) => {
    const parsed = Number(String(value ?? "0").replace(/[^\d.-]/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  if (isLoading) {
    return (
      <div className="ventureDashboard-empty">
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  if (!roleAllowed) {
    return (
      <div className="ventureDashboard-empty">
        <p>This dashboard is only available to venture accounts.</p>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="ventureDashboard-empty">
        <p>Missing auth token. Please sign in again.</p>
      </div>
    );
  }

  if (queryError) {
    return (
      <div className="ventureDashboard-empty">
        <p>{queryError.message || "Unable to load dashboard data."}</p>
      </div>
    );
  }

  if (!apiData) {
    return (
      <div className="ventureDashboard-empty">
        <p>No dashboard data available for your account.</p>
      </div>
    );
  }

  const name = apiData.name ?? "My Venture";
  const status = apiData.status ?? "active";
  const stage = apiData.stage ?? "";
  const normalizedStage = String(stage).toLowerCase();
  const stageLevel = STAGE_LEVELS[normalizedStage] ?? 0;
  const stageLabel = STAGE_LABELS[normalizedStage] ?? stage;
  const monthlyRevenue = apiData.monthly_revenue ?? "UGX 0";
  const revenueGrowth = apiData.revenue_growth?.value ?? "0%";
  const jobsFulltime = toNumber(apiData.jobs?.fulltime);
  const jobsPartime = toNumber(apiData.jobs?.partime);
  const capitalReceived = apiData.capital_received ?? "UGX 0";
  const repayment = apiData.repayment ?? "Not Available";

  // ---- Performance & Readiness derivation (mirror of admin page) --------
  //
  // The venture dashboard used to render a single simple performance/readiness
  // card driven off the /venture/dashboard payload (which currently returns
  // null values). Ventures with real due-diligence submissions in
  // ksync_due_diligence_scoring have their scores exposed via
  // /venture/due-diligence — we now surface those exactly as the admin page
  // does so a venture sees the same picture their admin sees.

  const performance = dueDiligenceApiData?.performance ?? null;
  const ideationOverallScore =
    performance?.ideation?.performance_score ??
    getPhaseOverallScore(dueDiligenceApiData?.rankings?.ideation);
  const postIdeationOverallScore =
    performance?.post_ideation?.performance_score_no_finance ??
    getPhaseOverallScore(dueDiligenceApiData?.rankings?.post_ideation);
  const hasIdeationScore =
    ideationOverallScore !== null && ideationOverallScore !== undefined;
  const hasPostIdeationScore =
    postIdeationOverallScore !== null && postIdeationOverallScore !== undefined;
  const hasAnyPhaseScore = hasIdeationScore || hasPostIdeationScore;

  const ideationPhase = hasIdeationScore
    ? derivePhase(ideationOverallScore)
    : null;
  const postIdeationPhase = hasPostIdeationScore
    ? derivePhase(postIdeationOverallScore)
    : null;

  // Fallback "single-view" values used when no phase-level DDS data exists.
  // We reuse the legacy dashboard payload here so ventures without DDS still
  // see something (even if it's currently 0/Not Assessed until backend fills
  // those fields).
  const legacyPerformancePct = toNumber(
    (apiData.performance?.score ?? "0/100").split("/")[0],
  );
  const fallbackBehavioralScore = Math.round(legacyPerformancePct * 0.88);
  const fallbackOverallWeightedScore = Math.round(
    legacyPerformancePct * 0.7 + fallbackBehavioralScore * 0.3,
  );
  const fallbackRiskLevel = getRiskLevel(legacyPerformancePct);
  const fallbackRiskMeta = getRiskMeta(fallbackRiskLevel);
  const fallbackReadinessKey =
    apiData.performance?.investment_readiness_key ??
    getReadiness(legacyPerformancePct);
  const fallbackReadinessLabel =
    apiData.performance?.investment_readiness ??
    READINESS_META[fallbackReadinessKey]?.label ??
    "Not Assessed";
  const fallbackReadinessTone =
    READINESS_META[fallbackReadinessKey]?.tone ?? "muted";

  const beneficiariesReached = toNumber(apiData.impact?.beneficiaries_reached);
  const qualityOfLifeScore = apiData.impact?.quality_of_life_score ?? null;
  const impactSubtitle = apiData.impact?.subtitle ?? "";

  return (
    <div className="ventureDashboard">
      {/* Header: venture name + status badges + Submit CTA */}
      <header className="ventureDashboard-header">
        <div>
          <h1 className="ventureDashboard-title">{name}</h1>
          <div className="ventureDashboard-badges">
            <span className="ventureDashboard-badge ventureDashboard-badge-primary">
              {stage}
            </span>
            <span className="ventureDashboard-badge ventureDashboard-badge-outline">
              {status}
            </span>
          </div>
        </div>
        <Link to="/venture/submit" className="ventureDashboard-cta">
          <Upload className="ventureDashboard-icon-sm" aria-hidden="true" />
          <span>Submit Data</span>
        </Link>
      </header>

      {/* Top KPI row — six compact stat cards */}
      <section
        className="ventureDashboard-kpis"
        aria-label="Key performance indicators"
      >
        <KpiCard icon={Wallet} label="Monthly Revenue" value={monthlyRevenue} />
        <KpiCard
          icon={TrendingUp}
          label="Revenue Growth"
          value={revenueGrowth}
          tone="success"
        />
        <KpiCard
          icon={Briefcase}
          label="Jobs"
          value={
            <span className="ventureDashboard-jobs">
              <span className="ventureDashboard-jobs-segment">
                <strong>{jobsFulltime}</strong>
                <span className="ventureDashboard-jobs-tag">full time</span>
              </span>
              <span className="ventureDashboard-jobs-segment">
                <strong>{jobsPartime}</strong>
                <span className="ventureDashboard-jobs-tag">part time</span>
              </span>
            </span>
          }
        />
        <KpiCard
          icon={Wallet}
          label="Capital Received"
          value={capitalReceived}
        />
        <KpiCard
          icon={CheckCircle2}
          label="Repayment"
          value={
            <span className="ventureDashboard-badge ventureDashboard-badge-outline">
              {String(repayment).replace("_", " ")}
            </span>
          }
        />
        <div className="ventureDashboard-kpi ventureDashboard-stageKpi">
          <span className="ventureDashboard-kpi-label"> Stage</span>
          <div className="ventureDashboard-stage-badgeRow">
            <Sprout className="ventureDashboard-icon-sm" aria-hidden="true" />
            <span className="ventureDashboard-badge ventureDashboard-badge-primary">
              {stageLevel > 0
                ? `Level ${stageLevel}: ${stageLabel}`
                : stageLabel || "-"}
            </span>
          </div>
          <div
            className="ventureDashboard-stage-bar"
            aria-label={`Stage progress: ${stageLevel} of 5`}
          >
            {[1, 2, 3, 4, 5].map((level) => (
              <span
                key={level}
                className={
                  "ventureDashboard-stage-dot" +
                  (level <= stageLevel
                    ? " ventureDashboard-stage-dot-active"
                    : "")
                }
              />
            ))}
          </div>
        </div>
      </section>

      {/* Performance & Readiness Scores — mirrors admin ventureDetail. When
          the venture has ideation and/or post-ideation DDS data, we show
          per-phase rings, scores, risk levels and readiness badges. When no
          DDS data exists at all, we fall back to a single-view card driven
          off the legacy dashboard payload. */}
      <section className="ventureDashboard-card">
        <header className="ventureDashboard-card-header">
          <h2 className="ventureDashboard-card-title">
            <Shield className="ventureDashboard-icon-sm" aria-hidden="true" />
            Performance & Readiness Scores
          </h2>
        </header>
        <div className="ventureDashboard-card-body">
          <div className="ventureDashboard-grid ventureDashboard-grid-5">
            <div className="ventureDashboard-scorePanel ventureDashboard-scorePanel-primary">
              <p className="ventureDashboard-panel-label">Overall Score</p>
              {hasAnyPhaseScore ? (
                <>
                  <p className="ventureDashboard-muted ventureDashboard-scoreMeta">
                    {hasIdeationScore && hasPostIdeationScore
                      ? "Post-ideation vs ideation"
                      : hasPostIdeationScore
                        ? "Post-ideation"
                        : "Ideation"}
                  </p>
                  <div className="ventureDashboard-ringGroup">
                    {hasPostIdeationScore && (
                      <ScoreRing
                        label="Post-ideation"
                        value={postIdeationOverallScore}
                        variant="post"
                      />
                    )}
                    {hasIdeationScore && (
                      <ScoreRing
                        label="Ideation"
                        value={ideationOverallScore}
                        variant="ideation"
                      />
                    )}
                  </div>
                </>
              ) : (
                <div className="ventureDashboard-ringWrap">
                  <svg viewBox="0 0 100 100" className="ventureDashboard-ring">
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      className="ventureDashboard-ringTrack"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      className="ventureDashboard-ringValue"
                      style={{
                        strokeDasharray: `${(fallbackOverallWeightedScore / 100) * 251} 251`,
                      }}
                    />
                  </svg>
                  <span className="ventureDashboard-ringText">
                    {fallbackOverallWeightedScore}
                  </span>
                </div>
              )}
            </div>

            <ScorePanel
              label="Performance"
              value={legacyPerformancePct}
              ideation={ideationPhase?.performance}
              postIdeation={postIdeationPhase?.performance}
              splitActive={hasAnyPhaseScore}
            />
            <ScorePanel
              label="Behavioral"
              value={fallbackBehavioralScore}
              ideation={ideationPhase?.behavioral}
              postIdeation={postIdeationPhase?.behavioral}
              splitActive={hasAnyPhaseScore}
            />

            <div className="ventureDashboard-scorePanel">
              <p className="ventureDashboard-panel-label">Risk Level</p>
              {hasAnyPhaseScore ? (
                <div className="ventureDashboard-phaseStack">
                  {postIdeationPhase && (
                    <div className="ventureDashboard-phaseRow">
                      <span className="ventureDashboard-muted ventureDashboard-scoreMeta">
                        Post - Ideation
                      </span>
                      <Badge tone={postIdeationPhase.riskMeta.tone}>
                        {postIdeationPhase.riskMeta.label}
                      </Badge>
                      <span className="ventureDashboard-muted ventureDashboard-scoreMeta">
                        Score: {Math.round(postIdeationPhase.performance)}
                      </span>
                    </div>
                  )}
                  {ideationPhase && (
                    <div className="ventureDashboard-phaseRow">
                      <span className="ventureDashboard-muted ventureDashboard-scoreMeta">
                        Ideation
                      </span>
                      <Badge tone={ideationPhase.riskMeta.tone}>
                        {ideationPhase.riskMeta.label}
                      </Badge>
                      <span className="ventureDashboard-muted ventureDashboard-scoreMeta">
                        Score: {Math.round(ideationPhase.performance)}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <Badge tone={fallbackRiskMeta.tone}>
                    {fallbackRiskMeta.label}
                  </Badge>
                  <p className="ventureDashboard-muted ventureDashboard-scoreMeta">
                    Score: {fallbackRiskMeta.score}
                  </p>
                </>
              )}
            </div>

            <div className="ventureDashboard-scorePanel">
              <p className="ventureDashboard-panel-label">
                Investment Readiness
              </p>
              {hasAnyPhaseScore ? (
                <div className="ventureDashboard-phaseStack">
                  {postIdeationPhase && (
                    <div className="ventureDashboard-phaseRow">
                      <span className="ventureDashboard-muted ventureDashboard-scoreMeta">
                        Post - Ideation
                      </span>
                      <Badge
                        tone={
                          READINESS_META[postIdeationPhase.readinessKey]
                            ?.tone ?? "muted"
                        }
                      >
                        {READINESS_META[postIdeationPhase.readinessKey]
                          ?.label ?? "Not Ready"}
                      </Badge>
                    </div>
                  )}
                  {ideationPhase && (
                    <div className="ventureDashboard-phaseRow">
                      <span className="ventureDashboard-muted ventureDashboard-scoreMeta">
                        Ideation
                      </span>
                      <Badge
                        tone={
                          READINESS_META[ideationPhase.readinessKey]?.tone ??
                          "muted"
                        }
                      >
                        {READINESS_META[ideationPhase.readinessKey]?.label ??
                          "Not Ready"}
                      </Badge>
                    </div>
                  )}
                </div>
              ) : (
                <Badge tone={fallbackReadinessTone}>
                  {fallbackReadinessLabel}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Impact summary */}
      <section className="ventureDashboard-grid-2">
        <article className="ventureDashboard-card">
          <header className="ventureDashboard-card-header">
            <h2 className="ventureDashboard-card-title">Your Impact</h2>
          </header>
          <div className="ventureDashboard-card-body">
            <div className="ventureDashboard-impact-row">
              <span className="ventureDashboard-impact-label">
                <Heart
                  className="ventureDashboard-icon-sm"
                  aria-hidden="true"
                />
                End Customers Reached
              </span>
              <span className="ventureDashboard-impact-value">
                {beneficiariesReached.toLocaleString()}
              </span>
            </div>
            <div className="ventureDashboard-impact-row">
              <span className="ventureDashboard-impact-label">
                Quality of Life Score
              </span>
              <span className="ventureDashboard-impact-value ventureDashboard-impact-value-accent">
                {qualityOfLifeScore ?? "0"}
              </span>
            </div>
            <p className="ventureDashboard-impact-note">{impactSubtitle}</p>
          </div>
        </article>

        <article className="ventureDashboard-card">
          <header className="ventureDashboard-card-header">
            <h2 className="ventureDashboard-card-title">Insights & Notes</h2>
          </header>
          <div className="ventureDashboard-card-body">
            <p className="ventureDashboard-impact-note">To be discussed</p>
          </div>
        </article>
      </section>
    </div>
  );
}

/* ---------------------------------------------------------------------- *
 * Small private subcomponents — kept in the same file because they're
 * only used here and are pure presentational helpers.
 * ---------------------------------------------------------------------- */

// Compact KPI card used in the top stats row.
function KpiCard({ icon: Icon, label, value, tone }) {
  const valueClass =
    "ventureDashboard-kpi-value" +
    (tone === "success" ? " ventureDashboard-kpi-value-success" : "");
  return (
    <div className="ventureDashboard-kpi">
      <div className="ventureDashboard-kpi-label">
        <Icon className="ventureDashboard-icon-sm" aria-hidden="true" />
        <span>{label}</span>
      </div>
      <div className={valueClass}>{value}</div>
    </div>
  );
}

// Tone-aware pill for score summaries. Kept local to avoid pulling in
// adminComponents (which would drag admin-wide CSS into the venture bundle).
function Badge({ tone = "default", children }) {
  return (
    <span className={`ventureDashboard-badge ventureDashboard-badge-${tone}`}>
      {children}
    </span>
  );
}

// Simple horizontal progress bar used inside the score panels.
function Progress({ value = 0, tone = "primary" }) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div className="ventureDashboard-progress-track">
      <div
        className={`ventureDashboard-progress-bar ventureDashboard-progress-${tone}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// Circular ring showing a single phase score (post-ideation or ideation).
function ScoreRing({ label, value, variant = "post" }) {
  const numeric = Number(value);
  const hasValue = Number.isFinite(numeric);
  const bounded = hasValue ? Math.max(0, Math.min(100, numeric)) : 0;

  return (
    <div className="ventureDashboard-ringItem">
      <div className="ventureDashboard-ringWrap">
        <svg viewBox="0 0 100 100" className="ventureDashboard-ring">
          <circle
            cx="50"
            cy="50"
            r="40"
            className="ventureDashboard-ringTrack"
          />
          <circle
            cx="50"
            cy="50"
            r="40"
            className={
              "ventureDashboard-ringValue" +
              (variant === "ideation"
                ? " ventureDashboard-ringValue-ideation"
                : " ventureDashboard-ringValue-post")
            }
            style={{
              strokeDasharray: `${(bounded / 100) * 251} 251`,
            }}
          />
        </svg>
        <span className="ventureDashboard-ringText">
          {hasValue ? Math.round(bounded) : "—"}
        </span>
      </div>
      <span className="ventureDashboard-muted ventureDashboard-ringLabel">
        {label}
      </span>
    </div>
  );
}

// Numeric score panel supporting phase-split (post-ideation + ideation) or
// a single fallback value.
function ScorePanel({ label, value, ideation, postIdeation, splitActive }) {
  const hasPost = postIdeation !== null && postIdeation !== undefined;
  const hasIdeation = ideation !== null && ideation !== undefined;

  if (splitActive && (hasPost || hasIdeation)) {
    return (
      <div className="ventureDashboard-scorePanel">
        <p className="ventureDashboard-panel-label">{label}</p>
        <div className="ventureDashboard-phaseStack">
          {hasPost && (
            <div className="ventureDashboard-phaseRow">
              <span className="ventureDashboard-muted ventureDashboard-scoreMeta">
                Post - Ideation
              </span>
              <p className="ventureDashboard-scoreBig">
                {Math.round(postIdeation)}
              </p>
              <Progress
                value={Math.max(0, Math.min(100, Number(postIdeation)))}
                tone="primary"
              />
            </div>
          )}
          {hasIdeation && (
            <div className="ventureDashboard-phaseRow">
              <span className="ventureDashboard-muted ventureDashboard-scoreMeta">
                Ideation
              </span>
              <p className="ventureDashboard-scoreBig">
                {Math.round(ideation)}
              </p>
              <Progress
                value={Math.max(0, Math.min(100, Number(ideation)))}
                tone="primary"
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="ventureDashboard-scorePanel">
      <p className="ventureDashboard-panel-label">{label}</p>
      <p className="ventureDashboard-scoreBig">{Math.round(value)}</p>
      <Progress value={Math.max(0, Math.min(100, value))} tone="primary" />
    </div>
  );
}
